import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

// Rounded, modern. Coral primary, soft secondary, ghost for tertiary actions.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "select-none whitespace-nowrap font-medium",
    "rounded-md transition-all",
    "duration-150 ease-[cubic-bezier(0.32,0.72,0,1)]",
    "disabled:opacity-40 disabled:pointer-events-none",
    "focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2",
  ],
  {
    variants: {
      intent: {
        primary:
          "bg-primary text-white hover:bg-primary-ink shadow-soft hover:shadow-lift",
        secondary:
          "bg-surface text-ink border border-rule hover:border-rule-strong hover:bg-surface-2",
        ghost:
          "text-ink-soft hover:text-ink hover:bg-surface-2",
        soft:
          "bg-primary-soft text-primary-ink hover:bg-primary/15",
        sage:
          "bg-sage-soft text-sage-ink hover:bg-sage/20",
        danger:
          "bg-danger text-white hover:opacity-90 shadow-soft",
      },
      size: {
        sm: "h-8 px-3 text-[0.82rem]",
        md: "h-10 px-4 text-[0.92rem]",
        lg: "h-12 px-6 text-[1rem]",
      },
      // Kept for back-compat with older sites that passed `tone`. No-op.
      tone: {
        sans: "",
        mono: "",
      },
    },
    defaultVariants: {
      intent: "secondary",
      size: "md",
      tone: "sans",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, intent, size, tone, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ intent, size, tone }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
